import { useTranslations } from '@vocab/react';
import {
  Button,
  ButtonIcon,
  Column,
  Columns,
  IconArrow,
  Inline,
  Textarea,
  Actions,
  Stack,
  Box,
} from 'braid-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CareerFeedAIAssistantContentFrame,
  CareerFeedAIAssistantContentFrameMobile,
} from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { CareerFeedMobileModal } from '../CareerFeedAIAssistantContent/CareerFeedMobileModal';
import { PrivacyAndCollectionNotice } from '../CareerFeedAIAssistantContent/PrivacyAndCollectionNotice';
import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import type { ErrorType } from '../types';

import translations from './.vocab';
import { FeedbackComponent } from './FeedbackComponent';
import { OptionsButtons } from './OptionsButtons';
import { useSuggestions } from './useSuggestions';

export type CareerFeedAssistantQuestionSuggestion = {
  label: string;
  nextPrompt: string;
  showFreeTextField?: boolean;
  isSkipButton?: boolean;
};

type CareerFeedAssistantQuestionProps = {
  message: string;
  suggestions: CareerFeedAssistantQuestionSuggestion[];
  isLoading: boolean;
  onSendMessage: (
    userFreeTextMessage: string,
    skip: boolean,
    selectedSuggestions: CareerFeedAssistantQuestionSuggestion[],
  ) => void;
  onSendFeedback: (sentiment: boolean) => void;
  showFreeTextFieldOnly?: boolean;
  sendMessageError?: ErrorType | null;
  isMobile?: boolean;
};

const CHARACTER_LIMIT_FOR_FREE_TEXT = 280;

type CareerFeedFreeTextInputProps = {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  changeKeys?: readonly unknown[];
};

const CareerFeedFreeTextInput = ({
  onSendMessage,
  isLoading,
  changeKeys = [],
}: CareerFeedFreeTextInputProps) => {
  const { t } = useTranslations(translations);
  const [inputField, setInputField] = useState('');

  useEffect(() => {
    setInputField('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, changeKeys);

  const onSendButtonClick = useCallback(() => {
    if (
      inputField.length > 0 &&
      !isLoading &&
      inputField.length <= CHARACTER_LIMIT_FOR_FREE_TEXT
    ) {
      onSendMessage(inputField);
    }
  }, [inputField, isLoading, onSendMessage]);

  return (
    <Columns space="small" alignY="center">
      <Column>
        <Textarea
          aria-label={t('message')}
          id="career-feed-free-text-area"
          value={inputField}
          onChange={(event) => setInputField(event.currentTarget.value)}
          characterLimit={CHARACTER_LIMIT_FOR_FREE_TEXT}
          disabled={isLoading}
        />
      </Column>
      <Column width="content">
        <ButtonIcon
          icon={<IconArrow direction="up" />}
          label={t('Send')}
          id="send-message"
          onClick={onSendButtonClick}
        />
      </Column>
    </Columns>
  );
};

export const CareerFeedAssistantQuestion = ({
  message,
  suggestions: _suggestions,
  isLoading,
  onSendMessage,
  onSendFeedback,
  showFreeTextFieldOnly,
  sendMessageError,
}: CareerFeedAssistantQuestionProps) => {
  const { t } = useTranslations(translations);

  const { suggestions, selectedSuggestions } = useSuggestions(_suggestions);

  const showFreeTextField = useMemo(
    () =>
      selectedSuggestions.find((suggestion) => suggestion.showFreeTextField),
    [selectedSuggestions],
  );

  const [selectedPositiveFeedback, setSelectedPositiveFeedback] =
    useState(false);
  const [selectedNegativeFeedback, setSelectedNegativeFeedback] =
    useState(false);

  const [sentFeedback, setSentFeedback] = useState(false);
  const { sequence } = useCareerFeedAIAssistant();

  useEffect(() => {
    setSelectedPositiveFeedback(false);
    setSelectedNegativeFeedback(false);
    setSentFeedback(false);
  }, [message]);

  const onNextButtonClick = useCallback(() => {
    if (selectedSuggestions.length) {
      const hasSkipButton = Boolean(
        selectedSuggestions.find(({ isSkipButton }) => isSkipButton),
      );

      onSendMessage('', hasSkipButton, selectedSuggestions);
    }
  }, [selectedSuggestions, onSendMessage]);

  const onSendFreeTextMessage = useCallback(
    (freeTextMessage: string) => {
      onSendMessage(freeTextMessage, false, selectedSuggestions);
    },
    [onSendMessage, selectedSuggestions],
  );

  const onSkipButtonClick = useCallback(() => {
    onSendMessage('', true, selectedSuggestions);
  }, [onSendMessage, selectedSuggestions]);

  const isFirstQuestion = sequence === 1;

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <CareerFeedMobileModal>
          <Box height="full">
            <CareerFeedAIAssistantContentFrameMobile
              headMessage={message}
              contentSection={
                <Stack space="medium">
                  {showFreeTextFieldOnly ? (
                    <Actions>
                      <Button
                        size="small"
                        onClick={onSkipButtonClick}
                        variant="ghost"
                        loading={isLoading}
                      >
                        {t('Skip')}
                      </Button>
                    </Actions>
                  ) : null}
                  {suggestions.length ? (
                    <Inline space="xsmall">
                      <OptionsButtons
                        options={suggestions}
                        disableOnClick={isLoading}
                      />
                    </Inline>
                  ) : null}
                  {!showFreeTextFieldOnly && (
                    <FeedbackComponent
                      positiveFeedbackAction={() => {
                        onSendFeedback(true);
                        setSelectedPositiveFeedback(true);
                        setSentFeedback(true);
                      }}
                      negativeFeedbackAction={() => {
                        onSendFeedback(false);
                        setSelectedNegativeFeedback(true);
                        setSentFeedback(true);
                      }}
                      selectedPositiveFeedback={selectedPositiveFeedback}
                      selectedNegativeFeedback={selectedNegativeFeedback}
                      sentFeedback={sentFeedback}
                    />
                  )}
                </Stack>
              }
              errorType={sendMessageError}
              ctaSection={
                showFreeTextFieldOnly || showFreeTextField ? (
                  <CareerFeedFreeTextInput
                    onSendMessage={onSendFreeTextMessage}
                    isLoading={isLoading}
                    changeKeys={[showFreeTextFieldOnly, showFreeTextField]}
                  />
                ) : (
                  <Stack space="small">
                    <Button
                      tone="brandAccent"
                      variant="soft"
                      loading={isLoading}
                      onClick={onNextButtonClick}
                    >
                      {t('Next')}
                    </Button>
                    {isFirstQuestion && <PrivacyAndCollectionNotice />}
                  </Stack>
                )
              }
              loading={isLoading}
            />
          </Box>
        </CareerFeedMobileModal>
      }
      tabletAndAboveView={
        <CareerFeedAIAssistantContentFrame
          headMessage={message}
          contentSection={
            <>
              {showFreeTextFieldOnly ? (
                <Actions>
                  <Button
                    size="small"
                    onClick={onSkipButtonClick}
                    variant="ghost"
                    loading={isLoading}
                  >
                    {t('Skip')}
                  </Button>
                </Actions>
              ) : null}
              {suggestions.length ? (
                <Inline space="xsmall">
                  <OptionsButtons
                    options={suggestions}
                    disableOnClick={isLoading}
                  />
                </Inline>
              ) : null}
              {!showFreeTextFieldOnly && (
                <FeedbackComponent
                  positiveFeedbackAction={() => {
                    onSendFeedback(true);
                    setSelectedPositiveFeedback(true);
                    setSentFeedback(true);
                  }}
                  negativeFeedbackAction={() => {
                    onSendFeedback(false);
                    setSelectedNegativeFeedback(true);
                    setSentFeedback(true);
                  }}
                  selectedPositiveFeedback={selectedPositiveFeedback}
                  selectedNegativeFeedback={selectedNegativeFeedback}
                  sentFeedback={sentFeedback}
                />
              )}
            </>
          }
          errorType={sendMessageError}
          ctaSection={
            showFreeTextFieldOnly || showFreeTextField ? (
              <CareerFeedFreeTextInput
                onSendMessage={onSendFreeTextMessage}
                isLoading={isLoading}
                changeKeys={[showFreeTextFieldOnly, showFreeTextField]}
              />
            ) : (
              <Box maxWidth="content">
                <Button
                  tone="brandAccent"
                  variant="soft"
                  loading={isLoading}
                  onClick={onNextButtonClick}
                >
                  {t('Next')}
                </Button>
              </Box>
            )
          }
          loading={isLoading}
        />
      }
    />
  );
};
